/**
 * Deep-link highlight: switch to Sent tab/section and scroll to prepared row.
 */

import { useEffect, useRef } from 'react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import type { SentSection, TabKind } from '../shared/types';

export function findSentSectionForPreparedDropId(
  sections: { sent: PreparedDrop[]; scheduled: PreparedDrop[]; killed: PreparedDrop[] },
  preparedId: string,
): SentSection | null {
  if (sections.killed.some((d) => d.id === preparedId)) return 'killed';
  if (sections.scheduled.some((d) => d.id === preparedId)) return 'scheduled';
  if (sections.sent.some((d) => d.id === preparedId)) return 'sent';
  return null;
}

type SentBuckets = {
  sent: PreparedDrop[];
  scheduled: PreparedDrop[];
  killed: PreparedDrop[];
};

export function usePreparedDropInvestigationHighlight(opts: {
  highlightPreparedDropId: string | null | undefined;
  onInvestigationFocusConsumed?: () => void;
  sentBySection: SentBuckets;
  filteredSentBySection: SentBuckets;
  activeTab: TabKind;
  sentSection: SentSection;
  setActiveTab: (t: TabKind) => void;
  setSentSection: (s: SentSection) => void;
}) {
  const {
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
    sentBySection,
    filteredSentBySection,
    activeTab,
    sentSection,
    setActiveTab,
    setSentSection,
  } = opts;

  const highlightScrollDoneRef = useRef<string | null>(null);

  useEffect(() => {
    if (!highlightPreparedDropId) highlightScrollDoneRef.current = null;
  }, [highlightPreparedDropId]);

  useEffect(() => {
    if (!highlightPreparedDropId) return;
    const actual = findSentSectionForPreparedDropId(sentBySection, highlightPreparedDropId);
    if (!actual) return;
    setActiveTab('sent');
    setSentSection(actual);
  }, [highlightPreparedDropId, sentBySection, setActiveTab, setSentSection]);

  useEffect(() => {
    if (!highlightPreparedDropId) return;
    if (highlightScrollDoneRef.current === highlightPreparedDropId) return;
    if (activeTab !== 'sent') return;
    const visible = filteredSentBySection[sentSection].some((d) => d.id === highlightPreparedDropId);
    if (!visible) return;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-prepared-drop-id="${highlightPreparedDropId}"]`);
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        highlightScrollDoneRef.current = highlightPreparedDropId;
        onInvestigationFocusConsumed?.();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [
    highlightPreparedDropId,
    activeTab,
    sentSection,
    filteredSentBySection,
    onInvestigationFocusConsumed,
  ]);
}
